import { useContext } from 'react'
import ProviderSectionContainer, {
  PROVIDER_SECTION_IDS,
} from 'app/ProviderDetails/ProviderSectionContainer'
import { If } from '@/utility_components/ControlStatements'
import { formatClinicalSupervisorName } from 'app/ProviderDetails/utils'
import { RudderEvent } from 'constants/rudderEvents'
import RudderStackContext from 'context/common/RudderStackContext'
import { getPractitionerType } from 'utils/common'
import logEvent from 'utils/logEvent'
import styles from './index.module.scss'

type EducationAndTrainingProps = {
  providerDetails: Provider
}

const SUPERVISED_CLINICIAN_TYPES: ReadonlySet<ProviderClinicianType> = new Set<ProviderClinicianType>(
  ['NP', 'RESIDENT']
)

export const getClinicalSupervisorProfilePath = (
  supervisor: ClinicalSupervisor
): string =>
  `/${getPractitionerType(
    supervisor.title
  )}/${supervisor.state.toLowerCase()}/${supervisor.slug}`

const canShowClinicalSupervisor = (
  providerDetails: Provider
): providerDetails is Provider & { clinicalSupervisor: ClinicalSupervisor } => {
  const { clinicianType, clinicalSupervisor } = providerDetails

  return (
    !!clinicianType &&
    SUPERVISED_CLINICIAN_TYPES.has(clinicianType) &&
    !!clinicalSupervisor?.firstName?.trim() &&
    !!clinicalSupervisor?.lastName?.trim() &&
    !!clinicalSupervisor?.title?.trim() &&
    !!clinicalSupervisor?.state?.trim() &&
    !!clinicalSupervisor?.slug?.trim()
  )
}

const EducationAndTraining: React.FC<EducationAndTrainingProps> = ({
  providerDetails,
}) => {
  const { yearsOfExperience, academics } = providerDetails
  const { degrees } = academics || {}
  const { rudderStackLoaded } = useContext(RudderStackContext)
  const clinicalSupervisor = canShowClinicalSupervisor(providerDetails)
    ? providerDetails.clinicalSupervisor
    : null
  const supervisorPath = clinicalSupervisor
    ? getClinicalSupervisorProfilePath(clinicalSupervisor)
    : ''

  // If any of the degree has showOnsummary and something from name or university then return true
  const hasDegrees = degrees?.some(
    (degree) => degree?.showOnSummary && (degree?.name || degree?.university)
  )

  if (!yearsOfExperience && !hasDegrees && !clinicalSupervisor) {
    return null
  }

  const handleSupervisorLinkClick = () => {
    if (!rudderStackLoaded || !supervisorPath) {
      return
    }

    logEvent(RudderEvent.PROVIDER_PROFILE_CLINICAL_SUPERVISOR_LINK_CLICKED, {
      provider_id: providerDetails.id,
      from_url: window.location.href,
      to_url: `${window.location.origin}${supervisorPath}`,
    })
  }

  return (
    <ProviderSectionContainer
      heading="Education and training"
      id={PROVIDER_SECTION_IDS.EDUCATION_AND_TRAINING}
    >
      <div className={styles.section_container}>
        <If condition={!!yearsOfExperience}>
          <dl className={styles.section_row}>
            <dt className="~font-gibson ~font-medium ~text-black ~text-lg">
              Years in practice
            </dt>
            <dd className={styles.section_value}>{yearsOfExperience} years</dd>
          </dl>
        </If>

        {hasDegrees &&
          degrees
            ?.filter((degree) => degree.showOnSummary)
            ?.map((degree) => (
              <div key={degree.name} className={styles.degree_fields}>
                {degree?.university && (
                  <dl className={styles.section_row}>
                    <dt className="~font-gibson ~font-medium ~text-black ~text-lg">
                      Graduating institute
                    </dt>
                    <dd className={styles.section_value}>
                      {degree.university}
                    </dd>
                  </dl>
                )}

                {degree?.name && (
                  <dl className={styles.section_row}>
                    <dt className="~font-gibson ~font-medium ~text-black ~text-lg">
                      Graduating degree
                    </dt>
                    <dd className={styles.section_value}>{degree.name}</dd>
                  </dl>
                )}
              </div>
            ))}

        <If condition={!!clinicalSupervisor}>
          <dl className={styles.section_row}>
            <dt className="~font-gibson ~font-medium ~text-black ~text-lg">
              Clinical Supervisor
            </dt>
            <dd className={styles.section_value}>
              <a
                href={supervisorPath}
                className={styles.supervisor_link}
                onClick={handleSupervisorLinkClick}
              >
                {clinicalSupervisor
                  ? formatClinicalSupervisorName(clinicalSupervisor)
                  : ''}
              </a>
            </dd>
          </dl>
        </If>
      </div>
    </ProviderSectionContainer>
  )
}

export default EducationAndTraining
